<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsCheck from '@/components/pds/PdsCheck.vue';
import PdsModal from '@/components/pds/PdsModal.vue';
import { ref, watch } from 'vue';
import { useOperator } from '../composables/useOperator';
import { formatPickup, formatTime } from '../format';
import {
  orderToClientTicket,
  orderToPrepSlip,
} from '../print/printer.receipts';
import { usePrinter } from '../print/usePrinter';
import type { PdsOrder } from '../types';
import ClientTicketPaper from './ClientTicketPaper.vue';
import PrepSlipPaper from './PrepSlipPaper.vue';
import PrinterStatus from './PrinterStatus.vue';

const props = defineProps<{ order: PdsOrder }>();
defineEmits<{ close: [] }>();

type Kind = 'prep' | 'client';

const { name, store, city } = useOperator();
const kind = ref<Kind>('prep');
const printedAt = formatTime(Date.now());
const printer = usePrinter();
const sent = ref(false);

watch(kind, () => (sent.value = false));

const print = async () => {
  sent.value = false;
  const now = Date.now();
  sent.value = await printer.print(
    kind.value === 'prep'
      ? orderToPrepSlip(props.order, now, name.value)
      : orderToClientTicket(props.order, store.value, now),
  );
};

const choices: { value: Kind; label: string; hint: string }[] = [
  {
    value: 'prep',
    label: 'Bon de préparation',
    hint: 'Les produits, les options et la note de la commande. Reste en atelier.',
  },
  {
    value: 'client',
    label: 'Ticket client',
    hint: 'Le récapitulatif et le code de retrait. Part avec la commande.',
  },
];
</script>

<template>
  <PdsModal size="xxl" @close="$emit('close')">
    <template #title>Imprimer</template>
    <template #subtitle>
      #{{ order.orderNumber ?? order.number }} · {{ order.label }} · retrait
      {{ formatPickup(order.dueAt) }}
    </template>

    <div
      class="grid grid-cols-1 items-start gap-5 px-5 py-6 lg:grid-cols-[1fr_420px]"
    >
      <div class="flex flex-col gap-3">
        <p
          class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
        >
          Que faut-il imprimer
        </p>
        <button
          v-for="choice in choices"
          :key="choice.value"
          type="button"
          role="radio"
          :aria-checked="kind === choice.value"
          class="bg-carte flex cursor-pointer items-center gap-[14px] rounded-[24px] px-4 py-4 text-left"
          :class="
            kind === choice.value
              ? 'border-encre border-2'
              : 'border-filet border'
          "
          @click="kind = choice.value"
        >
          <PdsCheck :checked="kind === choice.value" />
          <span class="flex flex-col gap-[2px]">
            <span class="text-[14px] leading-[20px] font-semibold">
              {{ choice.label }}
            </span>
            <span class="text-encre-douce text-[12px] leading-[17px]">
              {{ choice.hint }}
            </span>
          </span>
        </button>

        <PrinterStatus class="mt-3" />
        <p
          v-if="sent"
          role="status"
          class="text-succes-texte text-[12px] leading-[17px]"
        >
          Envoyé à l’imprimante.
        </p>
      </div>

      <div class="flex flex-col gap-[10px]">
        <p
          class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
        >
          Aperçu · 80 mm
        </p>
        <div class="max-h-[340px] overflow-hidden rounded-[24px]">
          <PrepSlipPaper
            v-if="kind === 'prep'"
            :order="order"
            :store="store"
            :printed-at="printedAt"
            :operator="name"
          />
          <ClientTicketPaper
            v-else
            :order="order"
            :store="store"
            :city="city"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <PdsButton variant="second" fit @click="$emit('close')">Retour</PdsButton>
      <PdsButton
        fit
        class="sm:ml-auto"
        :loading="printer.printing.value"
        :disabled="!printer.configured.value"
        :title="
          printer.configured.value
            ? undefined
            : 'Choisissez une imprimante dans les réglages'
        "
        @click="print"
      >
        {{ kind === 'prep' ? 'Imprimer le bon' : 'Imprimer le ticket' }}
      </PdsButton>
    </template>
  </PdsModal>
</template>
